import { useRef, useState } from 'react';

interface FileDropZoneProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  label?: string;
}

function FileDropZone({ 
  onFiles, 
  accept, 
  multiple = true, 
  label = 'Drag & drop files here, or click to select' 
}: FileDropZoneProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const openFileDialog = (): void => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFiles = (fileList: FileList | null): void => {
    if (!fileList) return;
    const filesArray = Array.from(fileList);
    if (onFiles) onFiles(filesArray);
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    handleFiles(event.dataTransfer?.files || null);
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    handleFiles(event.target.files);
    // reset input so selecting the same file again triggers onChange
    event.target.value = '';
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFileDialog();
    }
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        aria-label="File upload dropzone"
        onClick={openFileDialog}
        onKeyDown={onKeyDown}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={`App-filedropzone ${isDragging ? 'App-filedropzone-dragging' : ''}`}
      >
        <div className="App-filedropzone-label">
          {label}
        </div>
        <div className="App-filedropzone-accept">
          {accept ? `Accepted: ${accept}` : 'Any file type'}
        </div>
        <div className="App-filedropzone-icon">
          📁
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        accept={accept}
        multiple={multiple}
        onChange={onChange}
      />
    </div>
  );
}

export default FileDropZone;

